import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

// Shift standards editor — Task area, managers and owners (CJ 2026-08-09, D249).
//
// These are the "before you go" items every employee ticks at the end of every
// shift. Editing is gated to view_admin_panel rather than edit_tasks, because
// CJ asked for "managers and owners" and edit_tasks also includes shift leads —
// and a shift lead editing the standard they are about to be measured against
// is the wrong shape.
//
// Retiring a standard deactivates it; nothing here deletes. Ticks already
// recorded against a standard are evidence in a conversation about someone's
// work, and deleting the row would orphan them.
//
// Top-level component, not declared inside another render: a component defined
// in a render body is a new type every keystroke, so React remounts it and the
// text field loses focus per character.
export function ShiftStandardsSettings({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  const [rows, setRows] = useState(null);
  const [drafts, setDrafts] = useState({});     // id -> { title, description }
  const [adding, setAdding] = useState({ title: '', description: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await api('GET', '/api/admin/shift-standards', null, headers);
      setRows(Array.isArray(r?.standards) ? r.standards : []);
      setDrafts({});
    } catch (e) { setErr(e?.message || String(e)); }
  }, [currentUser.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function call(fn) {
    setBusy(true); setErr('');
    try { await fn(); await load(); }
    catch (e) {
      let m = e?.message || 'Failed';
      try { m = JSON.parse(m).error || m; } catch (_) {}
      setErr(m);
    } finally { setBusy(false); }
  }

  const draftOf = (r) => drafts[r.id] || { title: r.title, description: r.description || '' };
  const isDirty = (r) => {
    const d = drafts[r.id];
    return !!d && (d.title !== r.title || (d.description || '') !== (r.description || ''));
  };

  const save = (r) => {
    const d = draftOf(r);
    if (!d.title.trim()) return;
    return call(() => api('PUT', `/api/admin/shift-standards/${r.id}`,
      { title: d.title.trim(), description: d.description }, headers));
  };
  const setActive = (r, active) => call(() =>
    api('PUT', `/api/admin/shift-standards/${r.id}`, { is_active: active }, headers));
  const add = () => {
    if (!adding.title.trim()) return;
    return call(async () => {
      await api('POST', '/api/admin/shift-standards',
        { title: adding.title.trim(), description: adding.description, priority: (rows.length + 1) * 10 }, headers);
      setAdding({ title: '', description: '' });
    });
  };

  const field = { border: '1px solid #d0d4d9', borderRadius: 8, padding: '7px 10px',
                  fontSize: 13, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' };

  if (!rows) return <div className="card text-sm text-gray-500">Loading…</div>;

  const active = rows.filter((r) => r.is_active);
  const retired = rows.filter((r) => !r.is_active);

  return (
    <div className="space-y-3" style={{ maxWidth: 660 }}>
      <div className="card">
        <div className="text-base font-semibold">End-of-shift standards</div>
        <div style={{ fontSize: 12, color: '#757575', marginTop: 2 }}>
          Every employee ticks these before they leave, on every shift. Their name and the
          time are recorded against each one — which is what lets an issue be reported with
          proof rather than staying a grievance nobody will own.
        </div>
      </div>

      {err && <div className="card" style={{ borderLeft: '4px solid #c62828', fontSize: 13, color: '#a8323a' }}>{err}</div>}

      <div className="card">
        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, marginBottom: 8 }}>
          In use · {active.length}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {active.map((r) => (
            <div key={r.id} style={{ border: '1px solid #eee', borderRadius: 8, padding: '10px 12px' }}>
              <input value={draftOf(r).title} style={{ ...field, fontWeight: 700 }}
                onChange={(e) => setDrafts((c) => ({ ...c, [r.id]: { ...draftOf(r), title: e.target.value } }))} />
              <input value={draftOf(r).description} placeholder="Extra detail (optional)"
                style={{ ...field, marginTop: 6 }}
                onChange={(e) => setDrafts((c) => ({ ...c, [r.id]: { ...draftOf(r), description: e.target.value } }))} />
              {!draftOf(r).title.trim() && (
                <div style={{ fontSize: 12, color: '#a8323a', marginTop: 4 }}>A title is required.</div>
              )}
              <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                <button onClick={() => save(r)} disabled={busy || !isDirty(r) || !draftOf(r).title.trim()}
                  style={{ background: isDirty(r) ? 'var(--forest)' : '#e0e0e0', color: '#fff', border: 'none',
                           borderRadius: 7, padding: '6px 14px', fontSize: 12.5, fontWeight: 700,
                           cursor: isDirty(r) ? 'pointer' : 'default', fontFamily: 'inherit' }}>
                  Save
                </button>
                <button onClick={() => setActive(r, false)} disabled={busy} className="btn-secondary" style={{ fontSize: 12 }}>
                  Retire
                </button>
              </div>
            </div>
          ))}
          {active.length === 0 && (
            <div style={{ fontSize: 13, color: '#8a5a1f' }}>
              No standards are in use — nobody will see a “Before you go” list.
            </div>
          )}
        </div>
      </div>

      <div className="card">
        <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, marginBottom: 8 }}>Add a standard</div>
        <input value={adding.title} placeholder="e.g. Espresso machine backflushed" style={field}
          onChange={(e) => setAdding((c) => ({ ...c, title: e.target.value }))} />
        <input value={adding.description} placeholder="Extra detail (optional)" style={{ ...field, marginTop: 6 }}
          onChange={(e) => setAdding((c) => ({ ...c, description: e.target.value }))} />
        <button onClick={add} disabled={busy || !adding.title.trim()} className="btn-primary"
          style={{ fontSize: 12.5, marginTop: 8 }}>
          Add
        </button>
      </div>

      {retired.length > 0 && (
        <div className="card">
          <div style={{ fontSize: 12, color: '#6b7280', fontWeight: 700, marginBottom: 8 }}>
            Retired · {retired.length}
          </div>
          <div style={{ fontSize: 12, color: '#757575', marginBottom: 8 }}>
            Kept, not deleted — ticks already recorded against these are part of the record.
          </div>
          {retired.map((r) => (
            <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                     gap: 8, padding: '5px 0', fontSize: 13 }}>
              <span style={{ color: '#757575' }}>{r.title}</span>
              <button onClick={() => setActive(r, true)} disabled={busy} className="btn-secondary" style={{ fontSize: 11.5 }}>
                Put back
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
